import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook } from "lucide-react";
import { SiLine, SiUbereats, SiGoogle } from "react-icons/si";
import { useTranslation } from "react-i18next";

const DAY_ORDER = [
  ["monday", "mon"], ["tuesday", "tue"], ["wednesday", "wed"], ["thursday", "thu"],
  ["friday", "fri"], ["saturday", "sat"], ["sunday", "sun"],
];

export default function Footer({ settings }) {
  const s = settings || {};
  const { t } = useTranslation();
  const hours = s.hours_by_day || {};
  const todayIdx = (new Date().getDay() + 6) % 7;

  return (
    <footer
      className="relative pt-20 pb-10 border-t mt-12"
      style={{ borderColor: "#E8D9BE", background: "#FBEBD8" }}
      data-testid="footer"
    >
      <div className="absolute inset-0 mandala-bg pointer-events-none" />
      <div className="container-tulsi relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <h3 className="font-serif-display text-4xl text-gold-gradient mb-1">TULSI</h3>
            <p className="text-xs tracking-[0.3em] uppercase text-[#A90011] mb-3">杜爾希</p>
            <p className="text-sm text-[#6B4A4E] leading-relaxed">{s.tagline || "Authentic Indian meals, made with love."}</p>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <SocialIcon url={s.instagram_url} testId="social-instagram" icon={<Instagram size={18} />} label="Instagram" />
              <SocialIcon url={s.facebook_url} testId="social-facebook" icon={<Facebook size={18} />} label="Facebook" />
              <SocialIcon url={s.line_url} testId="social-line" icon={<SiLine size={18} />} label="LINE" />
              <SocialIcon url={s.uber_url} testId="social-uber" icon={<SiUbereats size={18} />} label="Uber Eats" />
              <SocialIcon url={s.google_review_url} testId="social-google" icon={<SiGoogle size={18} />} label="Google Reviews" />
            </div>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.25em] uppercase text-[#D11D21] mb-4">{t("footer.explore")}</h4>
            <ul className="space-y-2 text-sm">
              {[
                [t("nav.menu"), "/menu"], [t("nav.gallery"), "/gallery"],
                [t("nav.blog"), "/blog"],
                [t("nav.reserve"), "/reservations"], [t("nav.reviews"), "/reviews"],
              ].map(([l, h]) => (
                <li key={h}>
                  <Link to={h} className="text-[#6B4A4E] hover:text-[#A90011] transition-colors">{l}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.25em] uppercase text-[#D11D21] mb-4">{t("footer.visit")}</h4>
            <ul className="space-y-3 text-sm text-[#6B4A4E]">
              <li className="flex items-start gap-2"><MapPin size={14} className="mt-1 text-[#C9A227] flex-shrink-0" /> <span>{s.address || "Taichung"}</span></li>
              <li className="flex items-start gap-2"><Phone size={14} className="mt-1 text-[#C9A227]" /> <a href={`tel:${s.phone}`} data-testid="footer-phone">{s.phone}</a></li>
              <li className="flex items-start gap-2"><Mail size={14} className="mt-1 text-[#C9A227]" /> <a href={`mailto:${s.email}`} data-testid="footer-email">{s.email}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.25em] uppercase text-[#D11D21] mb-4">{t("footer.hours")}</h4>
            <ul className="space-y-1.5 text-xs">
              {DAY_ORDER.map(([day, sk], i) => {
                const isToday = i === todayIdx;
                const isClosed = (hours[day] || "").toLowerCase() === "closed";
                return (
                  <li key={day} className={`flex justify-between gap-3 ${isToday ? "text-[#A90011] font-medium" : "text-[#6B4A4E]"}`} data-testid={`hours-${day}`}>
                    <span className="uppercase tracking-wider">{t(`days.${sk}`)}{isToday && " ●"}</span>
                    <span className={isClosed ? "text-[#D11D21]" : ""}>{isClosed ? t("days.closed") : (hours[day] || "—")}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="gold-separator mt-12" />
        <p className="text-center text-xs tracking-[0.2em] uppercase text-[#8E5F65]">
          © {new Date().getFullYear()} TULSI · 杜爾希 · {t("footer.copyright")}
        </p>
      </div>
    </footer>
  );
}

function SocialIcon({ url, testId, icon, label }) {
  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer" data-testid={testId} title={label}
         className="p-2 rounded-full hover:bg-[#FFFAEF] text-[#A90011] transition-colors">
        {icon}
      </a>
    );
  }
  return (
    <span data-testid={`${testId}-disabled`} title={`${label} link not set yet`}
          className="p-2 rounded-full text-[#A90011]/30 cursor-not-allowed">
      {icon}
    </span>
  );
}
